'use client';

import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';

interface SegmentDef {
  key: string;
  label: ReactNode;
  /** Small count after the label. */
  badge?: number | string;
}

interface SegmentedProps {
  segments: SegmentDef[];
  value: string;
  onChange: (key: string) => void;
  disabled?: boolean;
  className?: string;
}

/** Linked radio-style button group — exactly one segment active. */
export function Segmented({ segments, value, onChange, disabled, className }: SegmentedProps) {
  return (
    <div
      role="radiogroup"
      className={cn(
        'corner-brand-sm thin-scroll inline-flex max-w-full items-center gap-0.5 overflow-x-auto border border-zinc-700 bg-zinc-900 p-0.5',
        className
      )}
    >
      {segments.map((seg) => {
        const active = seg.key === value;
        return (
          <button
            key={seg.key}
            type="button"
            role="radio"
            aria-checked={active}
            disabled={disabled}
            onClick={() => onChange(seg.key)}
            className={cn(
              'corner-brand-xs flex h-7 shrink-0 items-center gap-1.5 px-2.5 text-sm font-medium whitespace-nowrap transition-colors disabled:cursor-not-allowed disabled:opacity-50 pointer-coarse:h-10',
              active ? 'bg-zinc-700 text-zinc-50' : 'text-zinc-300 hover:text-zinc-50'
            )}
          >
            {seg.label}
            {seg.badge !== undefined && seg.badge !== 0 ? (
              <span className={cn('text-xs tabular-nums', active ? 'text-zinc-300' : 'text-zinc-500')}>
                {seg.badge}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}
